/**
 * Structured data (JSON-LD) and document head for the public publications list route.
 * Breadcrumb labels in JSON-LD use English (common practice); UI meta strings use i18n.
 */

import { env } from '@web/src/lib/env.client'
import i18n from '@web/src/lib/intl/i18n'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { absoluteUrlForPath, baseUrl } from '@web/src/lib/utilsClient'

const LIST_PATH = '/publication/list'

export type PublicationListSeoItem = {
  name: string
  path: string
  imageUrl?: string | null
}

export type PublicationListSeoLoaderData = {
  seoList: {
    items: PublicationListSeoItem[]
    totalCount: number
  }
}

type PublicationListSearchForSeo = {
  search?: string
  tags?: string[]
  sortBy?: string
  myPublications?: boolean
  needsReview?: boolean
}

/** True when URL search filters narrow the catalog (not “my publications” / review queues). */
function hasActivePublicationListFilters(search: PublicationListSearchForSeo): boolean {
  if (search.search?.trim()) return true
  if (search.tags?.length) return true
  return Boolean(search.sortBy)
}

function buildPublicationListBreadcrumbJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: absoluteUrlForPath('/') },
      { '@type': 'ListItem', position: 2, name: 'Discover Cliterature', item: absoluteUrlForPath(LIST_PATH) },
    ],
  }
}

function buildPublicationListItemListJsonLd(input: {
  hasFilters: boolean
  items: PublicationListSeoItem[]
  totalCount: number
}): Record<string, unknown> {
  const tr = i18n.t.bind(i18n)
  const base: Record<string, unknown> = {
    '@type': 'ItemList',
    numberOfItems: input.totalCount,
  }

  if (input.hasFilters) {
    base.name = tr('Filtered publication search results')
  }

  if (input.items.length > 0) {
    base.itemListElement = input.items.map((row, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: row.name,
      item: absoluteUrlForPath(row.path),
      ...(row.imageUrl ? { image: row.imageUrl } : {}),
    }))
  }

  return base
}

export function publicationListPageHeadFromLoaderData(
  loaderData: PublicationListSeoLoaderData | null | undefined,
  search: PublicationListSearchForSeo
) {
  const tr = i18n.t.bind(i18n)
  const publicationsDescription = tr(
    'Browse member-curated erotic books and sensual fiction on Cliterature. Filter by tags, sort by rating or author, and find your next read on SpicyFriends.'
  )
  const canonicalUrl = `${baseUrl()}${LIST_PATH}`
  const hasFilters = hasActivePublicationListFilters(search)

  const items = loaderData?.seoList.items ?? []
  const totalCount = loaderData?.seoList.totalCount ?? 0

  const searchResultsPage: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'SearchResultsPage',
    url: canonicalUrl,
    name: tr('Cliterature'),
    description: publicationsDescription,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${baseUrl()}${LIST_PATH}?search={query}`,
      },
      queryInput: 'required name=query',
    },
    mainEntity: buildPublicationListItemListJsonLd({ hasFilters, items, totalCount }),
  }

  const jsonLd: Record<string, unknown>[] = [searchResultsPage, buildPublicationListBreadcrumbJsonLd()]

  const ogImage = items[0]?.imageUrl

  return seo({
    title: `${tr('Discover Cliterature')} | ${env.VITE_APP_NAME}`,
    description: publicationsDescription,
    url: canonicalUrl,
    ...(ogImage ? { image: ogImage } : {}),
    robots: robotsMetaForEnvironment({
      production: 'index, follow',
      staging: 'noindex,nofollow',
    }),
    jsonLd,
  })
}
